import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { Radio as AntRadio } from 'antd';
import { colorsPalette } from '../../styles/colors-palette';
import { CheckboxChangeEvent } from 'antd/lib/checkbox/Checkbox';

type GroupValueType = string | number;
type RadioSize = 'large' | 'small' | 'middle';
type Option<ValueType extends GroupValueType> = {
  label: string | React.ReactNode;
  value: ValueType;
  disabled?: boolean;
  visible?: boolean;
};

interface ButtonGroupCustomStyles {
  itemColor?: string;
  selectedColor?: string;
  selectedBackground?: string;
  selectedBorderColor?: string;
  itemPadding?: string;
  itemFontSize?: string;
  itemWhiteSpace?: string;
}

interface ButtonProps<ValueType extends GroupValueType> {
  value?: ValueType;
  disabled?: boolean;
  size?: RadioSize;
  options: Array<Option<ValueType>>;
  onChange?: (value: ValueType) => void;
  className?: string;
  customStyles?: ButtonGroupCustomStyles;
  equalWidth?: boolean;
}

function ButtonGroup<ValueType extends GroupValueType>({
  value,
  options,
  disabled,
  size,
  onChange,
  className,
  customStyles,
  equalWidth,
}: ButtonProps<ValueType>): ReactElement {
  const antOnChange = (e: CheckboxChangeEvent) => {
    onChange && onChange(e.target.value);
  };

  const filterVisibleItems = (items: Array<Option<ValueType>>): Array<Option<ValueType>> => {
    return items?.reduce((result, item) => {
      if ('visible' in item && item.visible === false) {
        return result;
      } else {
        delete item.visible;
        result.push(item);
      }
      return result;
    }, [] as Array<Option<ValueType>>);
  };

  return (
    <StyledButtonGroup
      value={value}
      disabled={disabled}
      options={filterVisibleItems(options)}
      size={size ? size : 'large'}
      onChange={antOnChange}
      optionType={'button'}
      className={className}
      customstyles={customStyles}
      $equalWidth={equalWidth}
    />
  );
}

const StyledButtonGroup = styled(AntRadio.Group).withConfig({
  shouldForwardProp: prop => (prop as string) !== 'customstyles',
})<{ customstyles?: ButtonGroupCustomStyles; $equalWidth?: boolean }>`
  --antd-wave-shadow-color: ${colorsPalette.lightGray300}; // TODO: move into general styles

  & {
    display: flex;
    width: 100%;

    .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled):focus-within {
      box-shadow: 0 0 0 3px ${colorsPalette.lightGray100};
    }

    .ant-radio-button-wrapper {
      text-align: center;
      flex-grow: 1;
      flex-basis: ${props => (props.$equalWidth === true ? '0' : 'auto')};
      color: ${props => props.customstyles?.itemColor ?? colorsPalette.gray600};
      padding: ${props => props.customstyles?.itemPadding};
      font-size: ${props => props.customstyles?.itemFontSize};
      white-space: ${props => props.customstyles?.itemWhiteSpace};
    }

    .ant-radio-button-wrapper:hover {
      color: ${props => props.customstyles?.itemColor ?? colorsPalette.gray600};
      border-color: ${colorsPalette.gray200};
    }

    .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
      color: ${props => props.customstyles?.selectedColor ?? colorsPalette.gray600};
      background: ${props => props.customstyles?.selectedBackground ?? colorsPalette.gray100};
      border-color: ${props => props.customstyles?.selectedBorderColor ?? colorsPalette.gray200};
    }

    .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled):hover {
      color: ${props => props.customstyles?.selectedColor ?? colorsPalette.gray600};
      background: ${props => props.customstyles?.selectedBackground ?? colorsPalette.gray100};
      border-color: ${props => props.customstyles?.selectedBorderColor ?? colorsPalette.gray200};
    }

    .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled)::before {
      background-color: ${props =>
        props.customstyles?.selectedBorderColor ?? colorsPalette.gray200};
      &:hover {
        background-color: ${props =>
          props.customstyles?.selectedBorderColor ?? colorsPalette.gray200};
      }
    }

    .ant-radio-button-wrapper:not(:first-child):hover::before {
      background-color: ${colorsPalette.gray200};
    }

    .ant-radio-button-wrapper-checked:not(
        [class*=' ant-radio-button-wrapper-disabled']
      ).ant-radio-button-wrapper:first-child {
      border-right-color: ${props =>
        props.customstyles?.selectedBorderColor ?? colorsPalette.gray200};
    }

    .ant-radio-button-wrapper-checked:not(
        [class*=' ant-radio-button-wrapper-disabled']
      ).ant-radio-button-wrapper:last-child {
      border-left-color: ${props =>
        props.customstyles?.selectedBorderColor ?? colorsPalette.gray200};
    }
  }
`;

export default ButtonGroup;
